import { observer } from 'mobx-react';
import { DefaultButton, PrimaryButton } from 'office-ui-fabric-react/lib/Button';
import { Dialog, DialogFooter, DialogType } from 'office-ui-fabric-react/lib/Dialog';
import { autobind } from 'office-ui-fabric-react/lib/Utilities';
import strings from 'owa-strings/owa-options-strings';
import * as React from 'react';
import * as ReactDOM from 'react-dom';

const styles = require('./OptionConfirmationDialog.scss');

interface OptionConfirmationDialogProps extends ConfirmOptionNavigationProps {
    onClose: () => void;
}

interface OptionConfirmationDialogState {
    isShown: boolean;
}

/**
 * A Dialog to show three options: save your current changes, discard them, or go back and edit them.
 * This is implemented rather than using owa-confirm-dialog because that system doesn't support three options,
 * and would add unneeded complexity to that package for a one-off scenario.
 * Additionally, this may end up styled differently in the future, with stacked full-width buttons, rather than
 * using the default Dialog styling of a row of footer buttons.
 */
@observer
class OptionConfirmationDialog extends React.Component<
    OptionConfirmationDialogProps,
    OptionConfirmationDialogState
> {
    constructor(props: OptionConfirmationDialogProps) {
        super(props);
        this.state = {
            isShown: false,
        };
    }

    componentDidMount() {
        this.setState({
            isShown: true,
        });
    }

    render() {
        return (
            <Dialog
                isOpen={this.state.isShown}
                type={DialogType.normal}
                onDismiss={this.onDismiss}
                onDismissed={this.onDismissed}
                className={styles.dialog}
                title={strings.optionsConfirmationDialog_title}
                subText={strings.optionsConfirmationDialog_subtitle}>
                <DialogFooter>
                    <PrimaryButton
                        className={styles.footerButton}
                        text={strings.optionsConfirmationDialog_save}
                        onClick={this.onSave}
                        disabled={this.props.shouldDisableSaveButton}
                    />
                    <DefaultButton
                        className={styles.footerButton}
                        text={strings.optionsConfirmationDialog_discard}
                        onClick={this.onDiscard}
                    />
                    <DefaultButton
                        className={styles.footerButton}
                        text={strings.optionsConfirmationDialog_cancel}
                        onClick={this.onCancel}
                    />
                </DialogFooter>
            </Dialog>
        );
    }

    @autobind
    private onSave() {
        this.onDismiss();
        this.props.onSave();
    }

    @autobind
    private onDiscard() {
        this.onDismiss();
        this.props.onDiscard();
    }

    @autobind
    private onCancel() {
        this.onDismiss();
        if (this.props.onCancel) {
            this.props.onCancel();
        }
    }

    @autobind
    private onDismiss() {
        this.setState({
            isShown: false,
        });
    }

    @autobind
    private onDismissed() {
        this.props.onClose();
    }
}

let confirmOptionContainer: HTMLDivElement;

export interface ConfirmOptionNavigationProps {
    onSave: () => void;
    onDiscard: () => void;
    onCancel?: () => void;
    shouldDisableSaveButton?: boolean;
}

export default function confirmOptionNavigation(props: ConfirmOptionNavigationProps) {
    if (!confirmOptionContainer) {
        confirmOptionContainer = document.createElement('div');
        document.body.appendChild(confirmOptionContainer);
    }

    ReactDOM.render(
        <OptionConfirmationDialog
            {...props}
            onClose={() => {
                ReactDOM.unmountComponentAtNode(confirmOptionContainer);
            }}
        />,
        confirmOptionContainer
    );
}
